// Que set se definio en tiebreak.
//
// El grupo juega el tiebreak en 5-5 (el set queda 6-5) o en 6-6 (queda 7-6).
// Vive en nucleo/ porque lo usan las dos puntas: la pantalla de carga (para
// ofrecer anotar el tanteo) y el Worker (para validarlo). Si cada una tuviera
// su propia version, un dia dirian cosas distintas.

/** Los games en los que se juega el tiebreak. */
export const EMPATES_CON_TIEBREAK = [5, 6] as const;

export function esEmpateDeTiebreak(games: number): boolean {
  return (EMPATES_CON_TIEBREAK as readonly number[]).includes(games);
}

/** 6-5 o 7-6, de cualquiera de los dos lados. */
export function esSetDeTiebreak(a: number, b: number): boolean {
  return Math.abs(a - b) === 1 && esEmpateDeTiebreak(Math.min(a, b));
}

type ResultadoTiebreak = { ok: true; valor: { tb_a: number | null; tb_b: number | null } } | { ok: false; error: string };
const bien = (valor: { tb_a: number | null; tb_b: number | null }): ResultadoTiebreak => ({ ok: true, valor });
const mal = (error: string): ResultadoTiebreak => ({ ok: false, error });
function esEntero(v: unknown): v is number { return typeof v === 'number' && Number.isInteger(v); }

/**
 * El tanteo del tiebreak, que es opcional y solo se muestra.
 *
 * Reglas: lo gana quien ganó el set, se juega a 7, y si pasó de 7 fue porque
 * hubo que sacar dos de ventaja (8-6, 10-8). Se acepta 7-6 por si el grupo lo
 * juega a punto de oro. No se exige más que eso: es un grupo de amigos, no un
 * torneo, y un tanteo raro no cambia ningún número.
 */
export function tiebreakValido(
  games_a: number,
  games_b: number,
  tb_a: unknown,
  tb_b: unknown,
): ResultadoTiebreak {
  const vacio = (v: unknown) => v === undefined || v === null;
  if (vacio(tb_a) && vacio(tb_b)) return bien({ tb_a: null, tb_b: null });

  if (!esEntero(tb_a) || !esEntero(tb_b)) {
    return mal('El tanteo del tiebreak tiene que ser de los dos lados, en números.');
  }
  if (!esSetDeTiebreak(games_a, games_b)) {
    return mal('Solo un 6-5 o un 7-6 puede llevar tiebreak.');
  }
  if (tb_a < 0 || tb_b < 0 || tb_a > 30 || tb_b > 30) {
    return mal('Ese tanteo de tiebreak no parece real. Revisá los números.');
  }
  if (tb_a === tb_b) return mal('El tiebreak lo gana alguien: no puede quedar empatado.');

  const ganoSetA = games_a > games_b;
  const ganoTbA = tb_a > tb_b;
  if (ganoSetA !== ganoTbA) {
    return mal('El tiebreak lo tiene que ganar la misma pareja que ganó el set.');
  }

  const ganador = Math.max(tb_a, tb_b);
  const perdedor = Math.min(tb_a, tb_b);
  if (ganador < 7) return mal('El tiebreak se juega a 7: el que lo gana llega por lo menos a 7.');
  if (ganador > 7 && ganador - perdedor !== 2) {
    return mal('Si el tiebreak pasó de 7, se gana por dos puntos (8-6, 9-7…).');
  }

  return bien({ tb_a, tb_b });
}

